<template>
    <div class="row">
        <p>When choosing your option in the {{ scheme.name }}, it's important to keep in mind that two key uncertainties could affect your decision:</p>

        <SummaryRadioGroup v-model="selectedOption" :options="options">
            <template #extraContent="{ selected }">
                <help
                    :title="getHelpTitle(selected as ImportantInfoOption)"
                    :text="getHelpText(selected as ImportantInfoOption)" 
                    :link-text="getLinkText(selected as ImportantInfoOption)">
                </help>
            </template>
        </SummaryRadioGroup>
    </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import HelpText from "@/guiide/ts/helpText";
import Help from "@/guiide/components/common/Help.vue";
import FinalSalaryScheme from "@/guiide/ts/assets/finalsalary/finalSalaryScheme";
import SummaryRadioGroup from "../common/SummaryRadioGroup.vue";
import { useUIContent } from "@/guiide/ts/configuration/useUIContent";

enum ImportantInfoOption {
    Inflation,
    LifeExpectancy
}

const props = defineProps({
    scheme: { type: Object as () => FinalSalaryScheme, required: true }
});

const { htmlArray } = useUIContent();

const selectedOption = ref<ImportantInfoOption>(ImportantInfoOption.Inflation);

const options = [
    {
        type: ImportantInfoOption.Inflation,
        name: "Inflation",
        description: ["Inflation is the term used to describe the general rise in the price of goods and services over time. This means that, to maintain your current lifestyle, your income also needs to increase as prices go up."],
        icon: "bi-graph-up-arrow",
        abbreviation: ""
    },
    {
        type: ImportantInfoOption.LifeExpectancy,
        name: "Life expectancy",
        description: [`Life expectancy is the average number of years a person is expected to live. Your ${props.scheme.name} pays income for life, so you'll always receive a pension. However, life expectancy still matters when choosing options. This part of the tool assumes you have an average life expectancy. Later, we'll check how your retirement plan works if you live longer than average.`],
        icon: "bi-heart-pulse",
        abbreviation: ""
    }
];

function getHelpTitle(option: ImportantInfoOption): string {
    if (option == ImportantInfoOption.Inflation) {
        return "Inflation";
    } else if (option == ImportantInfoOption.LifeExpectancy) {
        return "Life expectancy";
    }

    return "";
}

function getHelpText(option: ImportantInfoOption): Array<string> {
    if (option == ImportantInfoOption.Inflation) {
        return htmlArray('dboptions.important-info.inflation.read-more.body');
    } else if (option == ImportantInfoOption.LifeExpectancy) {
        return htmlArray('dboptions.important-info.life-expectancy.read-more.body');
    }

    return [];
}

function getLinkText(option: ImportantInfoOption): string {
    switch (option) {
        case ImportantInfoOption.Inflation:
            return "Read more about inflation.";
        case ImportantInfoOption.LifeExpectancy:
            return "Read more about life expectancy.";
        default:
            return "";
    }
}
</script>
